import { type FlagSnapshot, useFlagSnapshot } from '@platform/feature-flags';
import { type AnyRouter, useRouter } from '@tanstack/react-router';

/**
 * Whether a pathname resolves to a route that exists and is enabled, reading the flag the route
 * declared via `definePageRoute`. Callers that advertise a destination ask this instead of restating
 * its flag, so the gate cannot drift from the route.
 *
 * `pathname` is the router's internal form, so basepath-free: `parseLocation` strips the mount prefix
 * before matching.
 */
export function isRouteAvailable(router: AnyRouter, flags: FlagSnapshot, pathname: string): boolean {
  const route = router.getMatchedRoutes(pathname).foundRoute;
  if (route === undefined) {
    return false;
  }
  const flag = route.options.staticData?.featureFlag;
  return flag === undefined || flags[flag] === true;
}

export function useRouteAvailable(): (pathname: string) => boolean {
  const router = useRouter();
  const flags = useFlagSnapshot();
  return (pathname) => isRouteAvailable(router, flags, pathname);
}
